import { ClockBaseIcon } from '@tableau/icons/ClockBaseIcon';
import { NotificationSuccessIcon } from '@tableau/icons/NotificationSuccessIcon';
import { ButtonType, ButtonWidget, CheckBoxWithLabelWidget, ToastType, ToastWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import TagHttpClient from '~src/httpClient/TAG/TagHttpClient';

import { hoursLeftToExpire, isCnameVerified } from '../helpers/CustomDomainHelper';
import { UpdateCustomDomainTagRequest } from '../serverAPI/requests/AddOrUpdateCustomDomainTagRequest';
import { ICustomDomainSettingResult } from '../serverAPI/requests/GetCustomDomainSettingTagRequest';
import { AddOrUpdateCustomDomainSettingRequest,CustomDomainSettingResponse,Status } from '../serverAPI/types/CustomDomainTypes';
import { clockIconStyle,customDomainButtonStyle, customDomainContentStyle, customDomainHeaderContainer, customDomainSubHeaderStyle, intermediateDomainTextStyle, intermediateDomainwarningStyle, lineIconBaseStyle, lineIconStyle, progressIconContent, stepIconDiabledStyle, stepIconStyle, subHeaderDisabledStyle, successIconStyle } from '../styles/CustomDomainStyles';

export interface ValidateCnameViewState {
    isUploading: boolean;
    checkBox: boolean;
  }

export interface ValidateCnameViewProps {
    customDomainSetting: CustomDomainSettingResponse;
    triggerFetchCustomDomain(): void;
  }

export class ValidateCnameView extends React.PureComponent<ValidateCnameViewProps, ValidateCnameViewState> {
  private progressStepStyle: React.CSSProperties = undefined;
  private subHeaderStyle: React.CSSProperties = undefined;

  public constructor(props: ValidateCnameViewProps) {
    super(props);
    this.state = {
      isUploading: false,
      checkBox: false
    };
  }

  private handleChangeWrapper = (): ((checked: boolean) => void) => {
    return (checked: boolean) => {
      this.setState({ checkBox: checked });
    };
  };

  private validateCname = (): void => {
    const customDomainSetting: AddOrUpdateCustomDomainSettingRequest = {
      site_luid: this.props.customDomainSetting.site_luid,
      custom_domain: this.props.customDomainSetting.custom_domain,
      status: Status.cnameVerificationRequested,
      tls_certificate: null,
      tls_certificate_private_key: null
    };

    this.setState({isUploading: true});
    TagHttpClient.sendTagRequest(new UpdateCustomDomainTagRequest(customDomainSetting)).then((res: ICustomDomainSettingResult)  => {
      if(res.errors && res.errors.length > 0) {
        ToastWidget.show({
          message: Messages.CustomDomainSetting_validateCnameOperationFailed(),
          testId: 'toast-validate-cname-operation-failed',
          toastType: ToastType.Error,
        });
      }
      else if(res.status === Status.cnameVerificationFailed) {
        ToastWidget.show({
          message: Messages.CustomDomainSetting_validationFailed(),
          testId: 'toast-validate-cname-failed',
          toastType: ToastType.Error,
        });
      }
       this.props.triggerFetchCustomDomain();
     }).catch(err => {
       ToastWidget.show({
        message: Messages.CustomDomainSetting_validateCnameOperationFailed(),
        testId: 'toast-validate-cname-operation-failed',
        toastType: ToastType.Error,
      });
     }).finally(() => {
      this.setState({isUploading: false});
     });
  };

  private renderIcon() {
    if(isCnameVerified(this.props.customDomainSetting?.status)) {
      return <NotificationSuccessIcon size={24} alt={Messages.backgroundJobStatusCompleted()} className={successIconStyle}/>;
    }
    if(this.props.customDomainSetting?.intermediate_domain) {
      return <div className={stepIconStyle} data-tb-test-id={'validateCname-step-icon'}><div className = {progressIconContent}>2</div></div>;
    }
      return <div className={stepIconDiabledStyle} data-tb-test-id={'validateCname-disabled-step-icon'}><div className = {progressIconContent}>2</div></div>;
  }

  //Widgets for this view should be disabled when the request has expired or when CNAME has been verified successfully.
  private isWidgetDisabled() {
    return isCnameVerified(this.props.customDomainSetting?.status) || hoursLeftToExpire(this.props.customDomainSetting?.request_initiation_date) <= 0;
  }

  private renderReqestExpirationText() {
    let hoursLeft = hoursLeftToExpire(this.props.customDomainSetting?.request_initiation_date);
    if(hoursLeft <= 0) {
      return <div className={intermediateDomainwarningStyle}>{Messages.CustomDomainSetting_expired()}</div>;
    }
    return <div className={intermediateDomainTextStyle}> {Messages.CustomDomainSetting_expirationDescription({HOURS_LEFT: hoursLeft.toString()})} </div>;
  }

  private renderValidateCnameView() {
    return (
          <>
            <div className={customDomainContentStyle}>
                <CheckBoxWithLabelWidget
                  checked={this.isWidgetDisabled() || this.state.checkBox}
                  style={{width: 350, marginLeft:2}}
                  handleChange={this.handleChangeWrapper()}
                  testId='checkbox-validate-cname'
                  label={Messages.CustomDomainSetting_validateLabelDescription()}
                  disabled={this.isWidgetDisabled()} />
              <div className={customDomainButtonStyle}>
                <ButtonWidget buttonType={ButtonType.Primary} showSpinner={this.state.isUploading} onClick={this.validateCname} testId='button-validate-cname-save' disabled={this.isWidgetDisabled() || !this.state.checkBox}>
                  {Messages.CustomDomainSetting_validateButton()}
                </ButtonWidget>
              </div>
            </div>
            {!isCnameVerified(this.props.customDomainSetting?.status) ?
            <div className={customDomainContentStyle}>
              <ClockBaseIcon
                size={12}
                alt=''
                className={clockIconStyle}
              />
              {this.renderReqestExpirationText()}
            </div> : null }
           </>
        );
  }

  //Sets styles and icons based on the state of Validate Cname view. 
  private setViewStyles() {
    if(isCnameVerified(this.props.customDomainSetting?.status)) {
      this.progressStepStyle = lineIconBaseStyle;
      this.subHeaderStyle = customDomainSubHeaderStyle;
    } else if (this.props.customDomainSetting?.intermediate_domain) {
      this.progressStepStyle = lineIconStyle;
      this.subHeaderStyle = customDomainSubHeaderStyle;
    } else {
      this.progressStepStyle = lineIconBaseStyle;
      this.subHeaderStyle = subHeaderDisabledStyle;
    }
  }

  public render() {
    return (
    <>
      <div className={customDomainHeaderContainer}>
        {this.renderIcon()}
        {this.setViewStyles()}
        <strong style={this.subHeaderStyle}>{Messages.CustomDomainSetting_validateLabel()}</strong>
      </div>
      <div style={this.progressStepStyle}>
        { this.props.customDomainSetting?.intermediate_domain ? this.renderValidateCnameView() : null }
      </div>
    </>
    );
  }
}
